import { lazy, Suspense } from 'react'
import { DropZone } from '@/components/home/DropZone'
import { Home } from '@/components/home/Home'
import { PropertiesBar } from '@/components/layout/PropertiesBar'
import { SearchBar } from '@/components/layout/SearchBar'
import { Toolbar } from '@/components/layout/Toolbar'
import { PageNav, TopBar } from '@/components/layout/TopBar'
import { UpdatePrompt } from '@/components/layout/UpdatePrompt'
import { Sidebar } from '@/components/sidebar/Sidebar'
import { BusyOverlay, Toasts } from '@/components/ui/Feedback'
import { Viewer } from '@/components/viewer/Viewer'
import { useAutosave } from '@/hooks/useAutosave'
import { useFileLaunch } from '@/hooks/useFileLaunch'
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { useDoc } from '@/store/documentStore'
import { useUi } from '@/store/uiStore'
import { Popover } from './components/ui/Popover'

const OrganizeView = lazy(() =>
  import('@/components/organize/OrganizeView').then((m) => ({ default: m.OrganizeView })),
)
const Dialogs = lazy(() => import('@/components/dialogs/Dialogs').then((m) => ({ default: m.Dialogs })))

function Workspace() {
  const compact = useMediaQuery('(max-width: 767px)')
  const sidebarOpen = useUi((s) => s.sidebarOpen)
  const organize = useUi((s) => s.organizeMode)
  const current = useUi((s) => s.currentPage)
  const total = useDoc((s) => s.pages.length)

  return (
    <div className="flex h-dvh flex-col overflow-hidden">
      <TopBar compact={compact} />
      {!compact && !organize && (
        <div className="relative z-30 flex shrink-0 justify-center border-line border-b bg-surface-2/70 px-3 py-1.5">
          <Toolbar />
        </div>
      )}
      <div className="relative flex min-h-0 flex-1">
        {organize ? (
          <Suspense fallback={null}>
            <OrganizeView />
          </Suspense>
        ) : (
          <>
            {sidebarOpen && (
              <>
                {compact && (
                  <button
                    type="button"
                    aria-label="Close sidebar"
                    className="absolute inset-0 z-20 animate-in bg-black/30"
                    onClick={() => useUi.getState().toggleSidebar(false)}
                  />
                )}
                <Sidebar overlay={compact} />
              </>
            )}
            <main className="relative min-w-0 flex-1">
              <Viewer />
              <SearchBar />
              {!compact && (
                <div className="pointer-events-none absolute inset-x-0 top-3 z-20 flex justify-center px-3 [&>*]:pointer-events-auto">
                  <PropertiesBar />
                </div>
              )}
              {compact && (
                <Popover
                  side="top"
                  align="end"
                  trigger={({ toggle, ref }) => (
                    <button
                      ref={ref}
                      type="button"
                      onClick={toggle}
                      className="absolute right-3 bottom-3 z-20 rounded-full border border-line bg-glass px-3 py-1.5 font-medium text-xs tabular-nums shadow-md backdrop-blur-xl"
                    >
                      {current + 1} / {total}
                    </button>
                  )}
                >
                  <div className="p-1">
                    <PageNav />
                  </div>
                </Popover>
              )}
            </main>
          </>
        )}
      </div>
      {compact && !organize && (
        <nav className="safe-bottom tips-up relative z-30 shrink-0 border-line border-t bg-surface">
          <PropertiesBar compact />
          <Toolbar compact />
        </nav>
      )}
    </div>
  )
}

export default function App() {
  const status = useDoc((s) => s.status)
  useKeyboardShortcuts()
  useAutosave()
  useFileLaunch()

  return (
    <>
      {status === 'ready' ? <Workspace /> : <Home />}
      <Suspense fallback={null}>
        <Dialogs />
      </Suspense>
      <Toasts />
      <BusyOverlay />
      <DropZone />
      <UpdatePrompt />
    </>
  )
}
